import React from "react";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const SHORT_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DEFAULT_START = "18:00";
const DEFAULT_END = "06:00";

// 24-hour times in 15-minute steps: 00:00, 00:15 ... 23:45.
const TIME_OPTIONS = Array.from({ length: 96 }, (_, i) => {
  const h = String(Math.floor(i / 4)).padStart(2, "0");
  const m = String((i % 4) * 15).padStart(2, "0");
  return `${h}:${m}`;
});

const selectClass =
  "flex-1 min-w-0 rounded-md px-2 py-1.5 text-sm border-0 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors duration-150 disabled:opacity-40";

// Coverage: which days and times the post must be staffed. An end time
// earlier than the start runs past midnight into the next day.
//
//   coverage: [{ day_of_week 0-6, start_time "HH:MM", end_time "HH:MM" }]
function PostCoverage({ coverage, onChange }) {
  const byDay = new Map(coverage.map((c) => [c.day_of_week, c]));

  const toggle = (day) => {
    if (byDay.has(day)) {
      onChange(coverage.filter((c) => c.day_of_week !== day));
    } else {
      // Start from another enabled day's hours so filling in a week is quick.
      const template = coverage[0] || { start_time: DEFAULT_START, end_time: DEFAULT_END };
      onChange(
        [...coverage, { day_of_week: day, start_time: template.start_time, end_time: template.end_time }].sort(
          (a, b) => a.day_of_week - b.day_of_week
        )
      );
    }
  };

  const setTime = (day, field, value) =>
    onChange(coverage.map((c) => (c.day_of_week === day ? { ...c, [field]: value } : c)));

  const timeSelect = (day, field, value, disabled, label) => (
    <select
      aria-label={label}
      className={selectClass}
      value={value}
      disabled={disabled}
      onChange={(e) => setTime(day, field, e.target.value)}
    >
      {TIME_OPTIONS.map((t) => (
        <option key={t} value={t}>
          {t}
        </option>
      ))}
    </select>
  );

  return (
    <div>
      <span className="block text-sm font-medium mb-1 text-gray-600 dark:text-gray-300">
        Coverage
      </span>
      <ul className="space-y-2">
        {DAYS.map((name, day) => {
          const c = byDay.get(day);
          // An end before the start finishes the following morning.
          const endsNextDay = c && c.end_time < c.start_time;
          return (
            <li key={name} className="flex items-center gap-2">
              <label className="flex w-32 shrink-0 items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-brand-primary focus:ring-brand-primary"
                  checked={!!c}
                  onChange={() => toggle(day)}
                />
                {name}
              </label>
              {timeSelect(day, "start_time", c?.start_time || DEFAULT_START, !c, `${name} start`)}
              <span className="text-xs text-gray-400">to</span>
              {timeSelect(day, "end_time", c?.end_time || DEFAULT_END, !c, `${name} end`)}
              {/* Always takes its space so the dropdowns line up across rows. */}
              <span className="w-10 shrink-0 text-xs text-gray-400">
                {endsNextDay ? `(${SHORT_DAYS[(day + 1) % 7]})` : ""}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export { PostCoverage };
